import useCrud from 'hooks/useCrud';
import { HTMLAttributes, useCallback, useRef } from 'react';
import {
  ConfigResponse,
  config as configApi,
  init,
  updateConfig,
  updateWatchConfig,
  watchConfig as watchConfigApi,
} from './service';
import { LoadingOutlined } from '@ant-design/icons';
import { Alert, Badge, Button, Modal, message } from 'antd';
import { useTranslate } from '@tolgee/react';
import { useMount } from 'ahooks';
import PolicyTable from './PolicyTable';
import useModal from 'hooks/useModal';
import PolicyDrawer from './PolicyDrawer';
import { FV } from './PolicyForm/share';
import { getSlateExecuteContext } from 'views/Aide/Scenario/PolicyForm';
import StatusSwitch from 'components/StatusSwitch';
import LimitForm, { Container as LimitContainer } from './LimitForm';
import { createDefaultInitialValues } from './PolicyForm';
import styled from '@emotion/styled';
import { isAeProduct } from 'productLine';
import { ReactComponent as Icon1 } from './assets/icon1.svg';
import { ReactComponent as Icon2 } from './assets/icon2.svg';
import { ReactComponent as Icon3 } from './assets/icon3.svg';
import Icon from '@ant-design/icons/lib/components/Icon';
import ReceptionForm from './ReceptionForm';

export interface NightWatchProps extends HTMLAttributes<HTMLDivElement> {
  initialValue?: ConfigResponse;
  platform?: General.PlatformUpperCase;
  region: General.UpperCaseRegion;
  storeGroupId: string | number;
  editable?: boolean;
}

function NightWatch(props: NightWatchProps) {
  const { initialValue, platform, region, storeGroupId, editable = true, ...otherProps } = props;

  const { t } = useTranslate();

  const itemRef = useRef<undefined | [ConfigResponse, NightWatch.WatchConfig]>();

  const { item, loading, fetch } = useCrud({
    read: useCallback(
      (props?: { needPolicy?: boolean; refreshWatchConfig?: boolean; needInit?: boolean }) => {
        if (!itemRef.current) {
          return Promise.all([
            initialValue
              ? Promise.resolve(initialValue)
              : init(storeGroupId).then(() =>
                  configApi({ groupId: storeGroupId, needPolicy: true }),
                ),
            watchConfigApi(storeGroupId),
          ]);
        }

        const {
          current: [{ policy: prePolicyList }, preWatchConfig],
        } = itemRef;

        const { needPolicy = false, refreshWatchConfig = false, needInit = false } = props || {};

        return Promise.all([
          (needInit ? init(storeGroupId) : Promise.resolve()).then(() =>
            configApi({ groupId: storeGroupId, needPolicy }).then((res) =>
              needPolicy ? res : { ...res, policy: prePolicyList },
            ),
          ),
          refreshWatchConfig ? watchConfigApi(storeGroupId) : Promise.resolve(preWatchConfig),
        ]);
      },
      [storeGroupId, initialValue],
    ),
  });

  itemRef.current = item;

  const refresh = (...props: Parameters<typeof fetch>) => {
    return fetch(...props).catch(() => {
      message.error(t('phrase.获取数据失败，请稍后再试'));
    });
  };

  useMount(() => {
    refresh();
  });

  const { visible, show, hide, meta } = useModal<FV>();

  if (!item) {
    return loading ? <LoadingOutlined /> : null;
  }

  const [{ policy: policyList, ...config }, watchConfig] = item;

  const { id, watchLimit } = watchConfig;

  const { fallbackId, enable } = config;

  const drawer = meta && (
    <PolicyDrawer
      visible={visible}
      platform={platform}
      region={region}
      initialValues={meta}
      policyList={policyList}
      onClose={() => hide()}
      onUpdated={() => refresh({ needPolicy: true })}
    />
  );

  const table = (
    <PolicyTable
      editable={editable}
      platform={platform}
      region={region}
      dataSource={policyList}
      onEdit={({ replyContent, ...others }) =>
        show({
          ...others,
          replyContent: replyContent.map((content) =>
            getSlateExecuteContext({
              content,
              translatedContent: '',
            }),
          ),
        })
      }
      onUpdated={() => refresh({ needPolicy: true })}
    />
  );

  return (
    <Container {...otherProps}>
      {!isAeProduct && (
        <header>
          <h2>{t('aide.值守设置')}</h2>
          <StatusSwitch
            disabled={!editable}
            checked={watchLimit?.enable}
            checkedChildren={t('aide.开')}
            unCheckedChildren={t('aide.关')}
            onChange={(enable) => {
              const doChagne = () =>
                Promise.all([
                  updateWatchConfig(id, {
                    watchLimit: { ...watchLimit, enable },
                  }),
                  updateConfig({
                    ...config,
                    enable,
                  }),
                ])
                  .then(() =>
                    refresh({
                      refreshWatchConfig: true,
                    }),
                  )
                  .then(() => {
                    message.success(t('operation.saveSucceed'));
                  })
                  .catch(() => {
                    message.error(t('operation.saveFailed'));
                  });

              if (enable) {
                return doChagne();
              }

              return new Promise<void>((resolve) => {
                Modal.confirm({
                  title: t('aide.确认要关闭值守设置吗？'),
                  content: t('aide.关闭值守设置tips'),
                  onOk: () => doChagne().finally(resolve),
                  onCancel: () => resolve(),
                });
              });
            }}
          />
          {!watchLimit?.enable && (
            <Alert banner showIcon type="info" message={t('aide.值守设置tips')} />
          )}
        </header>
      )}
      <div>
        <ul>
          <li>
            <div>01</div>
            <p>{t('aide.watchTips1')}</p>
            <Icon component={Icon1} style={{ fontSize: '48px' }} />
          </li>
          <li>
            <div>02</div>
            <p>{t('aide.watchTips2')}</p>
            <Icon component={Icon2} />
          </li>
          <li>
            <div>03</div>
            <p>{t('aide.watchTips3')}</p>
            <Icon component={Icon3} />
          </li>
        </ul>
        <section>
          <header>
            <h3>{t('aide.官方&店铺场景')}</h3>
          </header>
          <div>
            <dl>
              {t('aide.状态')}：
              {watchLimit?.enable ? (
                <Badge status="success" text={t('marketing.生效中')} />
              ) : (
                <Badge status="warning" text={t('marketing.未启用')} />
              )}
              {!watchLimit?.enable && (
                <Alert
                  banner
                  showIcon={false}
                  type="warning"
                  message={t('aide.值守模式官方&店铺场景未启用tips')}
                />
              )}
            </dl>
            <ReceptionForm
              editable={editable}
              config={watchConfig}
              onUpdated={() => refresh({ refreshWatchConfig: true })}
            />
          </div>
        </section>
        <section>
          <header>
            <h3>{t('aide.值守兜底回复')}</h3>
            <StatusSwitch
              disabled={!editable}
              checked={enable}
              checkedChildren={t('aide.开')}
              unCheckedChildren={t('aide.关')}
              onChange={(enable) => {
                return Promise.all([
                  updateConfig({ ...config, enable }),
                  enable && !isAeProduct
                    ? updateWatchConfig(id, { watchLimit: { ...watchLimit, enable } })
                    : Promise.resolve(),
                ])
                  .then(() =>
                    refresh({
                      refreshWatchConfig: enable && !isAeProduct,
                    }),
                  )
                  .then(() => {
                    message.success(t('operation.saveSucceed'));
                  })
                  .catch(() => {
                    message.error(t('operation.saveFailed'));
                  });
              }}
            />
          </header>
          <div>
            <dl>
              {t('aide.状态')}：
              {enable ? (
                <Badge status="success" text={t('marketing.生效中')} />
              ) : (
                <Badge status="warning" text={t('marketing.未启用')} />
              )}
              {!enable && (
                <Alert
                  banner
                  showIcon={false}
                  type="warning"
                  message={t('aide.值守兜底回复tips')}
                />
              )}
            </dl>
            <LimitForm editable={editable} config={config} onUpdated={() => refresh()} />
            <LimitContainer>
              <header>
                <h4>{t('aide.值守兜底回复内容')}</h4>
                <Button
                  disabled={!editable}
                  type="link"
                  onClick={() => show(createDefaultInitialValues({ fallbackId }))}
                >
                  {t('aide.新增策略分支')}
                </Button>
              </header>
              <div>{table}</div>
            </LimitContainer>
          </div>
        </section>
      </div>
      {drawer}
    </Container>
  );
}

const Container = styled.div`
  > * {
    background: var(--fett-bg-panel);
  }
  > header {
    display: flex;
    align-items: center;
    height: 56px;
    padding-left: 24px;
    margin-bottom: 1px;
    > * {
      flex: 0 0 auto;
      margin-right: 12px;
    }
    > h2 {
      margin-bottom: 0;
      font-size: 16px;
    }
    > .ant-alert {
      flex: 1 0 0;
      padding: 6px 12px;
      font-size: 12px;
      background: var(--ant-error-color-deprecated-bg);
      * {
        color: var(--ant-error-color);
      }
    }
  }
  > div {
    padding: 16px;
    > * + * {
      margin-top: 16px;
    }
    > ul {
      display: flex;
      align-items: stretch;
      gap: 16px;
      list-style: none;
      margin: 0;
      padding: 12px 16px;
      background: #9cadff;
      border-radius: 8px;
      > li {
        flex: 1 0 0;
        display: flex;
        justify-content: space-between;
        align-items: center;
        position: relative;
        padding: 8px 16px 8px 24px;
        border-radius: 6px;
        overflow: hidden;
        > p {
          flex: 1 0 0;
          margin-bottom: 0;
          white-space: pre-wrap;
          color: var(--fett-text-secondary);
        }
        > span {
          flex: 0 0 1;
          margin-left: 12px;
          font-size: 56px;
        }
        > div {
          position: absolute;
          top: 0;
          left: 0;
          width: 20px;
          height: 18px;
          line-height: 18px;
          text-align: center;
          font-size: 12px;
          color: #fff;
        }
      }
      > li:nth-child(1) {
        background: #fffbef;
      }
      > li:nth-child(2) {
        background: #f3fffc;
      }
      > li:nth-child(3) {
        background: #f4f8ff;
      }
      > li:nth-child(1) > div {
        background: #ffe49e;
      }
      > li:nth-child(2) > div {
        background: #b2e8dc;
      }
      > li:nth-child(3) > div {
        background: #b7d2ff;
      }
    }
    > section {
      background: var(--fett-bg-subtle);
      > header {
        display: flex;
        align-items: center;
        height: 48px;
        padding: 0 16px;
        border-bottom: solid 1px var(--fett-border-default);
        > h3 {
          position: relative;
          margin-bottom: 0;
          margin-right: 12px;
          font-size: 14px;
          text-indent: 10px;
          &::before {
            content: '';
            display: inline-block;
            position: absolute;
            top: 3px;
            left: 0;
            width: 4px;
            height: 16px;
            background: var(--ant-primary-color);
          }
        }
      }
      > div {
        padding: 16px;
        > * + * {
          margin-top: 16px;
        }
        > dl {
          display: flex;
          align-items: center;
          margin-bottom: 0;
          > * {
            flex: 0 0 auto;
          }
          > .ant-alert {
            flex: 1 0 0;
            margin-left: 12px;
            padding: 6px 12px;
            font-size: 12px;
          }
        }
      }
    }
  }
  [data-color-mode='dark'] & {
    > div > ul {
      background: #1a2040;
      > li {
        &:nth-child(1) {
          background: #2a2518;
        }
        &:nth-child(2) {
          background: #182a28;
        }
        &:nth-child(3) {
          background: #181e2a;
        }
        &:nth-child(1) > div {
          background: #4a3d18;
        }
        &:nth-child(2) > div {
          background: #1a3d38;
        }
        &:nth-child(3) > div {
          background: #1a2d4a;
        }
      }
    }
  }
`;

export default NightWatch;
